import styles from './ReportColumnFilterModal.module.scss'

import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next'
import Loading from 'common/services/Loading';
import ExportDataItemsService from 'api/exportDataItems/ExportDataItemsService';
import { ExportDataItemSearchCriteria } from 'api/exportDataItems/models/ExportDataItemSearchCriteria';
import { LOGGER_LOG_TYPE } from 'Config';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import toggleSwitchStyle from 'common/components/toggleSwitch/ToggleSwitch.module.scss'
import { ExportDataItemOrAdditionalDataSelectedDto } from 'api/exportDataItems/models/ExportDataItemSelectedDto';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import CloseButton from 'assets/svg/x-circle.svg'
import Button from 'common/components/button/Button';
import Box from 'common/components/box/Box';

type Props = {
	isOpen: boolean;
	setIsOpen: (status: boolean) => void;
	type: string;
	companyId: string;
	getReport: (type: string) => void;
}

const ReportColumnFilterModal: React.FC<Props> = ({ isOpen, setIsOpen, type, getReport, companyId }: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const [criteria, setCriteria] = useState<ExportDataItemSearchCriteria>({
		itemsPerPage: 5000,
		page: 1,
		orderBy: 'asc',
		orderColumn: 'eic.export_data_item_id',
		companyId: companyId,
		type: type
	});
	const [items, setItems] = useState<ExportDataItemOrAdditionalDataSelectedDto[]>([]);
	const [refresh, setRefresh] = useState(0);
	const [hoverIndex, setHoverIndex] = useState<number | null>(null);

	const closeModal = () => {
		setIsOpen(false)
	}

	const getData = async () => {

		try {
			Loading.show();
			const arr = await ExportDataItemsService.getItems(criteria);
			arr.forEach(i => {
				if(!i.additionalData) {
					i.label = t(`export_data_report.list.types.${i.label}` as any);
				} 
			}); 
 
			arr.sort((a, b) => {
				const aoeA = a.orderOnExport;
				const aoeB = b.orderOnExport;

				if (aoeA != null && aoeB != null) return aoeA - aoeB;
				if (aoeA != null) return -1;   // a fica antes
				if (aoeB != null) return 1;    // b fica antes

				return 0;
				//return a.label.localeCompare(b.label);
			}); 
 
			setItems([...arr]);
		} catch (error) {
			Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get export data item list`, error);
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
		}
		finally {
			Loading.hide();
		}
	}


	useEffect(() => {	
		criteria.type = type;
		getData();
	}, [type]);

	const onSave = async () => {
		await ExportDataItemsService.update(items);
		getReport(type);
		closeModal();
	}

	const renderLabelColumnCell = (item: ExportDataItemOrAdditionalDataSelectedDto) => {
		return (
			<div style={{ display: 'flex', flexDirection: 'row' }}>
				<div>
					{item.label}
				</div>
			</div>
		)
	}

	const renderCheckColCell = (row: ExportDataItemOrAdditionalDataSelectedDto) => {
		return (
			<div key={'active_' + row.value}>
				<label
					key={`list_${refresh}`}
					style={{ cursor: 'pointer', marginTop: 5 }}
					className={`${toggleSwitchStyle.toggleSwitch} ${toggleSwitchStyle.enabled}`}>
					<input
						type='checkbox'
						id={`checkbox_${row.value}_${row.selected}`}
						checked={row.selected}
						onChange={e => {
							row.selected = !row.selected;
							setRefresh(refresh + 1);
						}}
					/>
					<span className={toggleSwitchStyle.switch} />
				</label>
			</div>
		)
	} 

	const renderHeaderColumnCell = () => {
		return (
			<div className={styles.headerRow}>
				<div>#&nbsp;&nbsp;&nbsp;&nbsp;{t('export_data_report.list.label')}</div> 
				<div>{t('export_data_report.list.selected')}</div>
			</div>
		)
	}

	const handleDragStart = (e: any, index: number) => {
		e.dataTransfer.setData('drag-index', index);
	};

	const handleDrop = (e: any, dropIndex: number) => {
		const dragIndex = parseInt(e.dataTransfer.getData('drag-index'), 10);
		setHoverIndex(null);

		if (dragIndex === dropIndex) return;

		const updated = [...items];
		const [moved] = updated.splice(dragIndex, 1);
		updated.splice(dropIndex, 0, moved);
		setItems(updated);
	};

	const handleDragOver = (e: any, index: number) => {
		e.preventDefault(); // permite o drop
		setHoverIndex(index);
	};

	const handleDragLeave = () => {
		setHoverIndex(null);
	};
	
	return (
		<>
			{isOpen && (
				<>
					<div className={styles.overlay} />
					<div className={styles.modalContainer}>
						<Box>
							<ScreenTitle title={t('export_data_report.title')}>{t('export_data_report.title')}</ScreenTitle> 
							<div className={styles.scrollable}>
								{renderHeaderColumnCell()}
								{items.map((item, index) => (
									<div key={`${item.value}_${index}`}
										draggable
										onDragStart={e => handleDragStart(e, index)}
										onDragOver={e => handleDragOver(e, index)}
										onDrop={e => handleDrop(e, index)}
										onDragLeave={handleDragLeave}
										style={{
											display: 'grid', 
											alignItems: 'center',
											gridTemplateColumns: '1.5rem 0.9fr 2rem',   
											padding: 2,
											borderBottom: '1px solid lightgray',
											cursor: 'move',
											backgroundColor: hoverIndex === index ? '#f2f2f2' : '#fff',
										}}> 
											{<div style={{ display: 'flex', flexDirection: 'row' }}>
												<div>
													{index + 1}
												</div>
											</div>}
											{renderLabelColumnCell(item)}
											{renderCheckColCell(item)}
									</div>
								))} 
							</div>

							<ScreenHeaderButton
								icon={CloseButton}
								onClick={() => closeModal()}
								className={styles.closeButton}
							></ScreenHeaderButton>
						</Box>
						<div className={styles.buttonContainer}>
							<Button type='button' text={t('common.next')} onClick={() => onSave()} />
						</div>
					</div>

				</>
			)}
		</>
	)
}

export default ReportColumnFilterModal
